import { AdvertiserListComponent } from '../companies/advertiser-list/advertiser-list.component';
import { CompanyListComponent } from './../companies/company-list/components/company-list/company-list.component';
import { APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { Routes, RouterModule, Router } from '@angular/router';
import { PermissionGuard } from 'src/app/misc/guards/permission.guard';
import { AuthGuard } from 'src/app/misc/guards/auth.guard';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { WelcomeComponent } from '../welcome/welcome.component';
import { UnauthorizedComponent } from '../unauthorized/unauthorized.component';
import { ObjectType } from 'src/app/data/constants/object-type.enum';
import { CampaignEditComponent } from '../campaigns/campaign-edit/components/campaign-edit/campaign-edit.component';
import { CampaignSummaryComponent } from '../campaigns/campaign-edit/components/campaign-summary/campaign-summary.component';
import { CreativeEditComponent } from '../creatives/creative-edit/components/creative-edit/creative-edit.component';
import { ListDataResolver, CampaignDataResolver } from 'src/app/services/common-resolvers.service';
import { PlatForm } from 'src/app/data/constants/platform.enums';
import { environment } from 'src/environments/environment';
import { BrandCampaignListComponent } from '../advertisers/brand-campaign-list/brand-campaign-list.component';
import { BrandListComponent } from '../advertisers/brand-list/brand-list.component';

const routes: Routes = [
    {
        path: 'company',
        data: {
            objectType: ObjectType.Company
        },
        children: [
            { path: '', redirectTo: 'list', pathMatch: 'full' },
            {
                path: 'list',
                component: CompanyListComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            },
            {
                path: 'advertiser-list',
                component: AdvertiserListComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            }
        ]
    },
    {
        path: 'advertiser',
        data: {
            objectType: ObjectType.Advertiser
        },
        children: [
            { path: '', redirectTo: 'brand-list', pathMatch: 'full' },
            {
                path: 'brand-list',
                component: BrandListComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            },
            {
                path: 'campaign-list',
                component: BrandCampaignListComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            },
        ]
    },
    {
        path: 'campaign',
        data: {
            objectType: ObjectType.Campaign
        },
        children: [
            { path: '', redirectTo: 'list', pathMatch: 'full' },
            {
                path: 'list',
                component: DashboardComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            },
            {
                path: 'new',
                component: CampaignEditComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { campaignData: CampaignDataResolver }
            },
            {
                path: 'new/:campaignId',
                component: CampaignEditComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { campaignData: CampaignDataResolver }
            },
            {
                path: 'edit/:campaignId',
                component: CampaignEditComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { campaignData: CampaignDataResolver }
            },
            {
                path: 'summary/:campaignId',
                component: CampaignSummaryComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { campaignData: CampaignDataResolver }
            }
        ]
    },
    {
        path: 'creative',
        data: {
            objectType: ObjectType.Creative
        },
        children: [
            { path: '', redirectTo: 'list', pathMatch: 'full' },
            {
                path: 'list',
                component: DashboardComponent,
                canActivate: [AuthGuard, PermissionGuard],
                resolve: { cbaId: ListDataResolver }
            },
            { path: 'new', component: CreativeEditComponent, canActivate: [AuthGuard, PermissionGuard] }
        ]
    },
    {
        path: 'deal',
        data: {
            objectType: ObjectType.Deal
        },
        children: [
            { path: '', redirectTo: 'marketplace', pathMatch: 'full' },
            { path: 'marketplace', component: DashboardComponent, canActivate: [AuthGuard, PermissionGuard] }
        ]
    },
    { path: 'welcome', component: WelcomeComponent },
    {
        path: 'sign-up',
        children: [
            { path: '', data: { platform: PlatForm.Console }, loadChildren: () => import('../sign-up/sign-up.module').then(m => m.SignUpModule) },
            { path: '', data: { platform: PlatForm.MediaCorp }, loadChildren: () => import('../../../whiteLabel/mediaCorps/app/application/sign-up/sign-up.module').then(m => m.SignUpModule) }
        ],
        data: {
            disabled: environment.disableUserCreation
        }
    },
    { path: 'login', loadChildren: () => import('../login/login.module').then(m => m.LoginModule) },
    { path: 'unauthorized', component: UnauthorizedComponent, canActivate: [AuthGuard] }
];

function filterRoutes(r) {
    if (r.children) {
        r.children = r.children.filter(filterRoutes);
    }
    return r.data === undefined || ((r.data.platform === undefined || r.data.platform === environment.platform) && !r.data.disabled);
}

export function initRouters(injector: Injector) {
    return () => {
        return new Promise((resolve) => {
            const router: Router = injector.get(Router);
            const filteredRoutes = router.config.filter(filterRoutes);
            router.resetConfig(filteredRoutes);
            resolve(undefined);
        });
    };
}

@NgModule({
    imports: [RouterModule.forChild(routes)],
    exports: [RouterModule],
    providers: [
        { provide: APP_INITIALIZER, useFactory: initRouters, deps: [Injector], multi: true },
        CampaignDataResolver,
        ListDataResolver
    ]
})
export class RouteRoutingModule { }
